import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_MAX_H = 280;
const VIEWPORT_PAD = 8;

function useLatest(value) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

/**
 * Compact color picker with swatches in the list and on the trigger.
 * Renders the menu in a portal with fixed positioning so it is not clipped by overflow-x on tables.
 */
export function BeamTableColorPicker({
  anchorId,
  openAnchorId,
  setOpenAnchorId,
  value,
  colorName,
  colorCode,
  options,
  onSelect,
  disabled,
  placeholder = 'Choose color',
  allowClear = true,
  'aria-label': ariaLabel,
}) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, width: 260, maxHeight: MENU_MAX_H });
  const isOpen = openAnchorId === anchorId;
  const onSelectRef = useLatest(onSelect);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = Array.isArray(options) ? options : [];
    if (!q) return list;
    return list.filter((c) => {
      const name = String(c.name || '').toLowerCase();
      const id = String(c.id ?? '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [options, query]);

  const close = useCallback(() => {
    setOpenAnchorId(null);
    setQuery('');
  }, [setOpenAnchorId]);

  const positionMenu = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(320, Math.max(220, rect.width, 240));
    let left = rect.left;
    if (left + width > vw - VIEWPORT_PAD) left = vw - VIEWPORT_PAD - width;
    if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

    const spaceBelow = vh - rect.bottom - VIEWPORT_PAD;
    const spaceAbove = rect.top - VIEWPORT_PAD;
    const openDown = spaceBelow >= 160 || spaceBelow >= spaceAbove;
    let top;
    let bottom;
    let maxHeight;
    if (openDown) {
      top = rect.bottom + 4;
      bottom = undefined;
      maxHeight = Math.min(MENU_MAX_H, spaceBelow);
    } else {
      top = undefined;
      bottom = vh - rect.top + 4;
      maxHeight = Math.min(MENU_MAX_H, spaceAbove);
    }

    if (maxHeight < 120) maxHeight = Math.min(MENU_MAX_H, Math.max(spaceBelow, spaceAbove, 120));

    setMenuStyle({ top, bottom, left, width, maxHeight });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    positionMenu();
    const onWin = () => positionMenu();
    window.addEventListener('scroll', onWin, true);
    window.addEventListener('resize', onWin);
    const t = window.setTimeout(() => searchRef.current?.focus(), 30);
    return () => {
      window.removeEventListener('scroll', onWin, true);
      window.removeEventListener('resize', onWin);
      window.clearTimeout(t);
    };
  }, [isOpen, positionMenu]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, close]);

  const handlePick = (opt) => {
    onSelectRef.current(opt);
    close();
  };

  const handleClear = () => {
    onSelectRef.current(null);
    close();
  };

  const showChevron = (
    <svg className="h-3.5 w-3.5 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  const menuNode =
    isOpen &&
    createPortal(
      <div
        ref={menuRef}
        role="listbox"
        aria-label={ariaLabel || 'Colors'}
        className="fixed z-[200] flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl ring-1 ring-black/5"
        style={{
          top: menuStyle.top != null ? menuStyle.top : undefined,
          bottom: menuStyle.bottom != null ? menuStyle.bottom : undefined,
          left: menuStyle.left,
          width: menuStyle.width,
          maxHeight: menuStyle.maxHeight,
        }}
      >
        <div className="shrink-0 border-b border-slate-100 px-2 py-1.5">
          <input
            ref={searchRef}
            type="search"
            placeholder="Search colors…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-1 py-1 [scrollbar-width:thin]"
          onWheel={(e) => e.stopPropagation()}
        >
          {allowClear && (value || colorName) ? (
            <button
              type="button"
              role="option"
              className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-xs font-medium text-slate-500 hover:bg-slate-50"
              onClick={handleClear}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-dashed border-slate-200 text-[10px]">
                ∅
              </span>
              Clear color
            </button>
          ) : null}
          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-slate-500">No colors match.</p>
          ) : (
            filtered.map((c) => {
              const sel = String(value || '') === String(c.id);
              const code = c.colorCode || '#e2e8f0';
              return (
                <button
                  key={c.id}
                  type="button"
                  role="option"
                  aria-selected={sel}
                  className={[
                    'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left text-xs transition-colors',
                    sel ? 'bg-primary-50 text-primary-900' : 'text-slate-800 hover:bg-slate-50',
                  ].join(' ')}
                  onClick={() => handlePick(c)}
                >
                  <span
                    className="h-8 w-8 shrink-0 rounded-md border border-slate-200/80 shadow-inner"
                    style={{ backgroundColor: code }}
                    title={c.name || ''}
                  />
                  <span className="min-w-0 flex-1 truncate font-semibold leading-tight">{c.name || '—'}</span>
                  {sel ? (
                    <svg className="h-4 w-4 shrink-0 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </div>,
      document.body
    );

  return (
    <div className="relative w-full min-w-0">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => {
          if (disabled) return;
          setOpenAnchorId(isOpen ? null : anchorId);
        }}
        className={[
          'flex w-full min-w-0 items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-all sm:py-2',
          disabled
            ? 'cursor-not-allowed border-slate-100 bg-slate-50 opacity-60'
            : isOpen
              ? 'border-primary-400 bg-white ring-2 ring-primary-500/20'
              : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/80',
        ].join(' ')}
      >
        {colorCode ? (
          <span
            className="h-7 w-7 shrink-0 rounded-md border border-slate-300 shadow-inner sm:h-8 sm:w-8"
            style={{ backgroundColor: colorCode }}
          />
        ) : (
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-dashed border-slate-300 text-[10px] text-slate-400 sm:h-8 sm:w-8">
            ?
          </span>
        )}
        <span className="min-w-0 flex-1 truncate text-[10px] font-semibold leading-snug text-slate-800 sm:text-[11px]">
          {colorName || placeholder}
        </span>
        {showChevron}
      </button>
      {menuNode}
    </div>
  );
}
